import React, { useEffect, useState } from "react";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Form,
  Input,
  Button,
  message,
  Switch,
  InputNumber,
  Tag,
  Image,
} from "antd";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import UploadFoodImage from "../../../lib/UploadFoodImage";
import {
  useListService,
  useServiceDelete,
  useServiceSave,
} from "../../../hooks/api/service/hooks";
import { IServiceList, IServicePayload } from "../../../types/api/service";
import { getSingleImageUrl, removeSpecialChars } from "../../../lib/utils";
import CommonListCard from "../../../views/components/reuseComp/CommonListCard";
import UploadImage from "../../../views/components/UploadImage";
import TextArea from "antd/es/input/TextArea";

interface ServiceListingSectionProps {
  allowURLStateUpdate?: boolean;
}

const ServiceListingSection: React.FC<ServiceListingSectionProps> = ({
  allowURLStateUpdate = true,
}) => {
  const [messageApi, contextHolder] = message.useMessage();

  const DEFAULT_NUQS_CONFIG = {
    throttleMs: !allowURLStateUpdate ? Infinity : 0,
  };

  const { data, isFetching } = useListService({
    page: 1,
    limit: 20,
    ...DEFAULT_NUQS_CONFIG,
  });

  const serviceList: IServiceList[] = data?.data || [];
  const qData = data?.qData ?? { page: 1, limit: 20 };

  const { mutate: saveService, isPending } = useServiceSave({ qData });

  const { mutate: deleteService } = useServiceDelete({ qData });

  const [editingService, setEditingService] = useState<IServiceList | null>(
    null,
  );

  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm();

  const watchHeading = Form.useWatch("heading", form);

  useEffect(() => {
    if (!editingService?._id) {
      form.setFieldValue("slug", removeSpecialChars(watchHeading));
    }
  }, [watchHeading, editingService, form]);

  useEffect(() => {
    if (editingService) {
      form.setFieldsValue({
        heading: editingService.heading,
        slug: editingService.slug,
        status: editingService.status,
        metaDescription: editingService.metaDescription,
        order: editingService.order,
        image: editingService.image,
        icon: editingService.icon,
      });
      setEditing(true);
    }
  }, [editingService, form]);

  const handleCancel = () => {
    setEditingService(null);
    setEditing(false);
    form.resetFields();
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const payload: IServicePayload = {
        ...editingService,
        slug: values.slug,
        order: values.order,
        status: values.status,
        metaDescription: values.metaDescription,
        image: getSingleImageUrl(values.image) || editingService?.image || "",
        icon: getSingleImageUrl(values.icon) || editingService?.icon || "",
        banner: {
          ...editingService?.banner,
          heading: values.heading,
        },
      };

      console.log("Payload is: ", payload)
      saveService(payload, {
        onSuccess: () => {
          messageApi.success("Service saved successfully");
          handleCancel();
        },
        onError: () => {
          messageApi.error("Failed to save service");
        },
      });
    } catch (error) {
      console.log("Validation failed:", error);
    }
  };

  const handleDelete = (id: string) => {
    deleteService(
      { ids: id },
      {
        onSuccess: () => {
          messageApi.success("Service deleted successfully");
        },
        onError: () => {
          messageApi.error("Failed to delete service");
        },
      },
    );
  };

  return (
    <div>
      {contextHolder}

      {editing ? (
        <>
          <div className="bg-[#F9F9F9] p-6 rounded-2xl shadow-sm mb-6">
            <Form
              layout="vertical"
              form={form}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <Form.Item
                label="Heading"
                name="heading"
                rules={[
                  {
                    required: true,
                    message: "Please enter heading",
                  },
                ]}
              >
                <Input placeholder="Heading" />
              </Form.Item>
              <Form.Item
                label="Meta Description"
                name="metaDescription"
                rules={[
                  {
                    required: true,
                    message: "Please enter meta description",
                  },
                ]}
              >
                <TextArea placeholder="please enter meta description..." />
              </Form.Item>

              <Form.Item
                label="Slug"
                name="slug"
                rules={[
                  {
                    required: true,
                    message: "Please enter slug",
                  },
                ]}
              >
                <Input placeholder="Slug" disabled={!!editingService?._id} />
              </Form.Item>

              <Form.Item
                label="Status"
                name="status"
                valuePropName="checked"
                rules={[
                  {
                    required: true,
                    message: "Please select status",
                  },
                ]}
              >
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>

              <Form.Item
                label="Order"
                name="order"
                rules={[
                  {
                    required: true,
                    message: "Please enter order",
                  },
                ]}
              >
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                name="image"
                rules={[
                  {
                    required: true,
                    message: "At least 1 image is required",
                  },
                ]}
                className="sm:col-span-2"
              >
                <UploadFoodImage maxUploadCount={1} />
              </Form.Item>

              <Form.Item
                label="Icon"
                name="icon"
                rules={[
                  {
                    required: true,
                    message: "please upload icon",
                  },
                ]}
                className="sm:col-span-2"
              >
                <UploadImage listType="picture-card" />
              </Form.Item>
            </Form>
          </div>

          <div className="flex gap-3">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={isPending}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper onClick={handleCancel}>Cancel</ButtonWrapper>
          </div>
        </>
      ) : (
        <>
          <div className="flex justify-end items-center mb-4 px-2">

            <Button
              type="primary"
              icon={<PlusOutlined/>}
              onClick={() => {
                setEditingService(null);
                setEditing(true);
                form.resetFields();
              }}
            >
              Add Service
            </Button>
          </div>

          <div className="rounded-[20px] overflow-hidden">
            <div className="hidden sm:block">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-primary text-white">
                    <th className="py-3 px-4 text-left">Order</th>
                    <th className="py-3 px-4 text-left">Image</th>
                    <th className="py-3 px-4 text-left">Icon</th>
                    <th className="py-3 px-4 text-left">Heading</th>
                    <th className="py-3 px-4 text-left">Status</th>
                    <th className="py-3 px-4 text-right">Action</th>
                  </tr>
                </thead>

                <tbody>
                  {serviceList.map((service) => (
                    <tr
                      key={service._id}
                      className="bg-[#F9F9F9] border-b hover:bg-gray-50 transition"
                    >
                      <td className="py-3 px-4">{service.order}</td>

                      <td className="py-3 px-4">
                        <Image
                          src={service.image || "/placeholder.png"}
                          alt={service.heading}
                          width={60}
                          height={60}
                        />
                      </td>

                      <td className="py-3 px-4">
                        <Image
                          src={service.icon || "/placeholder.png"}
                          alt={service.heading}
                          width={40}
                          height={40}
                        />
                      </td>

                      <td className="py-3 px-4">{service.heading}</td>

                      <td className="py-3 px-4">
                        <Tag color={service.status ? "green" : "red"} bordered>
                          {service.status ? "Active" : "Inactive"}
                        </Tag>
                      </td>

                      <td className="py-3 px-4 text-right">
                        <div className="flex justify-end gap-4 text-primary">
                          <EditOutlined
                            onClick={() => setEditingService(service)}
                            className="cursor-pointer text-[20px]"
                          />

                          <DeleteOutlined
                            onClick={() => handleDelete(service._id!)}
                            className="cursor-pointer text-[20px] hover:text-red-500"
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="sm:hidden space-y-4">
              {serviceList.map((service) => (
                <CommonListCard
                  key={service._id}
                  service={service}
                  setEditingService={setEditingService}
                  setEditing={setEditing}
                  handleDelete={handleDelete}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ServiceListingSection;
